import React, { createContext, useContext, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { CheckCircle, Loader2 } from "lucide-react";
import { submitLead } from "../lib/api";
import { SERVICE_OPTIONS } from "../data/site";
import { Starburst } from "./common";

const LeadContext = createContext(null);
export const useLead = () => useContext(LeadContext);

const VARIANT_META = {
  quote: {
    title: "Request a Free Quote",
    desc: "Tell us about your space and we'll get back to you with fast, honest pricing.",
    cta: "Send My Request",
  },
  booking: {
    title: "Book Online",
    desc: "Pick a preferred date and service — we'll confirm your booking and details shortly.",
    cta: "Request Booking",
  },
  contact: {
    title: "Get in Touch",
    desc: "Have a question? Send us a message and we'll respond quickly.",
    cta: "Send Message",
  },
  careers: {
    title: "Join the Jet Cleaning Team",
    desc: "Tell us about yourself. We're always looking for dependable, detail-oriented cleaners.",
    cta: "Apply Now",
  },
};

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  preferred_date: "",
  frequency: "",
  message: "",
};

export const LeadProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState("quote");
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const openLead = (v = "quote") => {
    setVariant(v);
    setForm(emptyForm);
    setDone(false);
    setOpen(true);
  };

  const meta = VARIANT_META[variant];
  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, source: variant });
      setDone(true);
      toast.success("Thanks! We received your request.");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(() => ({ openLead }), []);

  const showService = variant === "quote" || variant === "booking";
  const showDate = variant === "booking";
  const showFrequency = variant === "quote" || variant === "booking";

  return (
    <LeadContext.Provider value={value}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-[520px] rounded-3xl border-none p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
          data-testid="lead-dialog"
        >
          <div className="bg-jet-teal px-7 py-6 relative overflow-hidden">
            <Starburst className="absolute -right-6 -top-6 h-28 w-28 text-white/15" />
            <DialogHeader className="text-left space-y-1.5">
              <DialogTitle className="font-display text-2xl md:text-3xl font-semibold text-white">
                {done ? "Request Received" : meta.title}
              </DialogTitle>
              <DialogDescription className="text-white/85 text-sm md:text-base">
                {done
                  ? "We'll be in touch shortly. Need something urgent? Give us a call."
                  : meta.desc}
              </DialogDescription>
            </DialogHeader>
          </div>

          {done ? (
            <div className="px-7 py-10 flex flex-col items-center text-center gap-4">
              <CheckCircle className="h-16 w-16 text-jet-teal" strokeWidth={1.5} />
              <p className="text-muted-foreground">
                Thank you, {form.name.split(" ")[0] || "there"}. Your{" "}
                {meta.title.toLowerCase()} is on its way to our team.
              </p>
              <Button
                onClick={() => setOpen(false)}
                className="rounded-full bg-jet-ink hover:bg-jet-ink/90 h-12 px-8"
                data-testid="lead-dialog-close"
              >
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="px-7 py-6 space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="lead-name">Full Name *</Label>
                  <Input
                    id="lead-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    className="h-12 rounded-xl"
                    data-testid="lead-input-name"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="lead-email">Email *</Label>
                    <Input
                      id="lead-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      className="h-12 rounded-xl"
                      data-testid="lead-input-email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lead-phone">Phone *</Label>
                    <Input
                      id="lead-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(940) 000-0000"
                      className="h-12 rounded-xl"
                      data-testid="lead-input-phone"
                    />
                  </div>
                </div>

                {showService && (
                  <div className="space-y-1.5">
                    <Label>Service Needed</Label>
                    <Select value={form.service} onValueChange={update("service")}>
                      <SelectTrigger
                        className="h-12 rounded-xl"
                        data-testid="lead-select-service"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {showDate && (
                    <div className="space-y-1.5">
                      <Label htmlFor="lead-date">Preferred Date</Label>
                      <Input
                        id="lead-date"
                        type="date"
                        value={form.preferred_date}
                        onChange={update("preferred_date")}
                        className="h-12 rounded-xl"
                        data-testid="lead-input-date"
                      />
                    </div>
                  )}
                  {showFrequency && (
                    <div className="space-y-1.5">
                      <Label>Frequency</Label>
                      <Select
                        value={form.frequency}
                        onValueChange={update("frequency")}
                      >
                        <SelectTrigger
                          className="h-12 rounded-xl"
                          data-testid="lead-select-frequency"
                        >
                          <SelectValue placeholder="How often?" />
                        </SelectTrigger>
                        <SelectContent>
                          {["One-Time", "Weekly", "Bi-Weekly", "Monthly", "As Needed"].map(
                            (f) => (
                              <SelectItem key={f} value={f}>
                                {f}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="lead-message">
                    {variant === "careers"
                      ? "Tell us about your experience"
                      : "Anything else we should know?"}
                  </Label>
                  <Textarea
                    id="lead-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={3}
                    placeholder={
                      variant === "careers"
                        ? "Your cleaning experience, availability, and areas..."
                        : "Property size, timeline, special requests..."
                    }
                    className="rounded-xl resize-none"
                    data-testid="lead-input-message"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 rounded-full bg-jet-teal hover:bg-jet-teal-deep text-white font-bold text-base teal-glow"
                data-testid="lead-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Sending...
                  </>
                ) : (
                  meta.cta
                )}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </LeadContext.Provider>
  );
};
